<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useNotify } from '@/composables/notify'
import { useReportColumnTemplates } from '@/composables/report-column-template'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import PrintOptionToggles from '@/components/shared/PrintOptionToggles.vue'
import PrintColumnsEditor from '@/components/accounting/PrintColumnsEditor.vue'
import PrintSectionToggles from '@/components/accounting/PrintSectionToggles.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  // usePrintSettings() result; null hides the saved settings
  settings: {
    type: Object,
    required: false,
    default: null,
  },
  // [{ key, label, value, disable? }]
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  hideSections: {
    type: Boolean,
    required: false,
    default: false,
  },
  typeName: {
    type: String,
    required: false,
    default: '',
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'update:option', 'applied'])

const i18n = getI18n()
const { notifySuccess } = useNotify()
const { templateById, columnCount } = useReportColumnTemplates()

const showSave = ref(false)
const showUpdate = ref(false)
const newName = ref('')

const onSelected = (id) => {
  props.settings.apply(templateById(id))
  emit('applied')
}
const onOpenSave = () => {
  newName.value = props.settings.template.value ? i18n.t('common.copyOf', { name: props.settings.template.value.label }) : ''
  showSave.value = true
}
const onSaveAsync = async() => {
  await props.settings.saveAsync(newName.value.trim() || i18n.t('common.copyOf', { name: props.settings.template.value?.label ?? props.typeName }))
  notifySuccess(i18n.t('info.createdSuccessfully'))
  emit('applied')
}
const onUpdateAsync = async() => {
  await props.settings.updateAsync()
  notifySuccess(i18n.t('info.updatedSuccessfully'))
}
</script>
<template>
  <div>
    <q-select
      v-if="settings"
      class="q-mb-sm"
      :model-value="settings.template.value?.id ?? null"
      :options="settings.templateOptions.value"
      :label="i18n.t('common.report.columnSelection')"
      :hint="settings.isModified.value ? i18n.t('common.report.basedOn', { name: settings.template.value.label }) : undefined"
      :disable="disable"
      option-value="id"
      option-label="label"
      emit-value
      map-options
      dense
      @update:model-value="onSelected"
    >
      <template #after>
        <q-btn
          v-if="settings.isModified.value && !disable"
          flat
          round
          dense
          icon="mdi-restore"
          @click="onSelected(settings.template.value.id)"
        >
          <q-tooltip>{{ i18n.t('common.restore') }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="settings.isModified.value && settings.canUpdate.value"
          flat
          round
          dense
          color="positive"
          icon="mdi-content-save-outline"
          @click="showUpdate = true"
        >
          <q-tooltip>{{ i18n.t('common.report.updateTemplate', { name: settings.template.value.label }) }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="settings.canSave.value"
          flat
          round
          dense
          color="primary"
          icon="mdi-plus-circle-outline"
          @click="onOpenSave"
        >
          <q-tooltip>{{ i18n.t('common.report.saveAsNewTemplate') }}</q-tooltip>
        </q-btn>
      </template>
    </q-select>
    <print-section-toggles
      v-if="!hideSections"
      :model-value="modelValue"
      :disable="disable"
      @update:model-value="value => emit('update:modelValue', value)"
    />
    <print-option-toggles v-if="options.length" :options="options" @update="(key, value) => emit('update:option', key, value)" />
    <slot />
    <q-expansion-item
      v-if="columnCount(modelValue)"
      class="print-content-columns q-mt-sm"
      icon="mdi-table-column"
      :label="i18n.t('common.column', 2)"
      :caption="i18n.t('common.report.columnsCount', columnCount(modelValue))"
      default-opened
      dense
    >
      <print-columns-editor
        class="q-pa-sm"
        :model-value="modelValue"
        :disable="disable"
        @update:model-value="value => emit('update:modelValue', value)"
      />
    </q-expansion-item>
    <confirm-dialog
      v-model="showSave"
      :title="i18n.t('common.report.saveAsNewTemplate')"
      icon="mdi-plus-circle-outline"
      :message="i18n.t('info.reportColumnTemplateSaveAsNew', { type: typeName })"
      :label="i18n.t('common.create')"
      @confirm="onSaveAsync"
    >
      <q-input v-model="newName" :label="i18n.t('common.name')" autofocus />
    </confirm-dialog>
    <confirm-dialog
      v-if="settings"
      v-model="showUpdate"
      :title="i18n.t('common.report.updateTemplate', { name: settings.template.value?.label })"
      icon="mdi-content-save-outline"
      :message="i18n.t('info.reportColumnTemplateUpdate', { count: settings.template.value?.report_count ?? 0 })"
      :label="i18n.t('common.update')"
      @confirm="onUpdateAsync"
    />
  </div>
</template>
<style scoped lang="scss">
.print-content-columns {
  border: 1px solid $grey-4;
  border-radius: $generic-border-radius;
}
</style>
